import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { auth, User } from '@lib/firebase';
import { useNavigate } from 'react-router-dom';

interface BackendError {
  message: string;
  statusCode: number;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  backendError: BackendError | null;
  signIn: (email: string, password: string, returnUrl?: string) => Promise<void>;
  signUp: (email: string, password: string, displayName?: string, returnUrl?: string) => Promise<void>;
  signOut: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [backendError, setBackendError] = useState<BackendError | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Skip Firebase auth for content authentication pages
    // Content auth uses OptiPrime OAuth + JWT cookies, not Firebase
    if (window.location.pathname.startsWith('/auth/content-')) {
      setLoading(false);
      return;
    }

    // Subscribe to Firebase auth state changes
    const unsubscribe = auth.onAuthStateChanged((user, error) => {
      setUser(user);
      setLoading(false);

      // Store backend sync error if present
      if (error) {
        setBackendError({
          message: error.message,
          statusCode: error.statusCode,
        });
      } else {
        setBackendError(null);
      }
    });

    // Listen for embed auth events (from OptiSignsBridge SYNC_AUTH)
    const handleEmbedAuth = (e: Event) => {
      const detail = (e as CustomEvent).detail as User;
      setUser(detail);
      setLoading(false);
    };
    window.addEventListener('embed-auth', handleEmbedAuth);

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
      window.removeEventListener('embed-auth', handleEmbedAuth);
    };
  }, []);

  const signIn = async (email: string, password: string, returnUrl?: string) => {
    try {
      const data = await auth.signIn(email, password);
      setUser(data.user);

      // Validate return_url is a relative path (security: prevent open redirect)
      const destination = returnUrl && returnUrl.startsWith('/') ? returnUrl : '/dashboard';
      navigate(destination);
    } catch (error: any) {
      console.error('Sign in error:', error);
      throw error;
    }
  };

  const signUp = async (email: string, password: string, displayName?: string, returnUrl?: string) => {
    try {
      const data = await auth.signUp(email, password, displayName);
      setUser(data.user);

      // Validate return_url is a relative path (security: prevent open redirect)
      const destination = returnUrl && returnUrl.startsWith('/') ? returnUrl : '/dashboard';
      navigate(destination);
    } catch (error: any) {
      console.error('Sign up error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    // Redirect to OptiPrime logout (page will navigate away immediately)
    // This clears SSO session across all OptiPrime-connected apps
    await auth.signOut();
    // Note: Code after this line won't execute due to window.location.href redirect
  };

  const getIdToken = async (): Promise<string | null> => {
    try {
      if (user) {
        return await auth.getIdToken();
      }
      return null;
    } catch (error) {
      console.error('Get ID token error:', error);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, backendError, signIn, signUp, signOut, getIdToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}